import { CheckBox, ClearSharp, WarningRounded } from "@mui/icons-material";
import { subDays } from "date-fns/subDays";

/**
 * Truncates a given string to a specified length and appends "..." if truncation occurs.
 *
 * @param {string} [str=""] - The input string to be truncated.
 * @param {number} [limit=50] - The maximum allowed length of the string before truncation.
 * @returns {string} - The truncated string with "..." appended if needed.
 */
const truncateString = (str = "", limit = 50) =>
  str.length > limit ? str.slice(0, limit - 3) + "..." : str;

/**
 * Returns a CSS class based on the given priority level.
 *
 * @param {string} priority - The priority level ("high", "medium", "low").
 * @returns {string} - The corresponding CSS class for styling.
 */
export const getPriorityClass = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "hover:bg-primary/80 bg-red-100 text-red-700 border-red-200";
    case "medium":
      return "hover:bg-primary/80 bg-blue-100 text-blue-700 border-blue-200";
    case "low":
      return "hover:bg-primary/80 bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "";
  }
};

/**
 * Truncates a list of LOB by limiting it to two items and appending "..." if there are more.
 *
 * @param {string} lob - A comma-separated string of SKUs.
 * @returns {string} - The truncated SKU list with "..." appended if there are more than two items.
 */
export const truncateLob = (lob) => {
  if (!lob) return "";
  const parts = lob.split(",");
  if (parts.length > 2) {
    return `${parts.slice(0, 2).join(", ")}...`;
  }
  return lob; // If it's already 2 or fewer items, return as is
};

/**
 * Returns the date range for the given filter option.
 *
 * @param {string} filterOption - The selected date filter option.
 * @returns {{ startDate: string, endDate: string }} - ISO date strings for start and end.
 */
export const getDateRange = (filterOption) => {
  const now = new Date();
  const endDate = new Date(now.setHours(0, 0, 0, 0)); // Reset time to midnight
  const startDate = subDays(
    endDate,
    filterOption === "last_3_days" ? 2 : filterOption === "last_7_days" ? 6 : 29
  ); // Adjust subtraction

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

/**
 * Formats a key string by replacing underscores with spaces and capitalizing the first letter of each word.
 *
 * @param {string} key - The key string to format.
 * @returns {string} - The formatted key string.
 */
export const formatKey = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Returns an icon based on the given score.
 *
 * @param {number} score - The score to evaluate.
 * @returns {JSX.Element} - The corresponding icon component.
 */
// import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
export const getIcon = (score) => {
  if (score >= 7) return <CheckBox className="text-green-500 bg-opacity-25" />; // Green Checkmark

  if (score >= 4)
    return <WarningRounded className="text-orange-500 bg-opacity-25" />; // Yellow Warning

  return <ClearSharp className="text-red-600" />;
  // Red Cross
};

/**
 * Checks if a value is not empty.
 *
 */
export const isNotEmpty = (value) => {
  if (!value) return false; // Handles null, undefined, false, 0, "" (empty string)
  if (Array.isArray(value)) return value.length > 0; // Check if array is not empty
  if (typeof value === "object") return Object.keys(value).length > 0; // Check if object has keys
  return true; // For non-empty strings or other valid values
};

/**
 * Transforms the appetite data to a more readable format.
 *
 * @param {Array<Record<string, any>>} appetiteData - The raw appetite data to transform.
 * @returns {Array<Record<string, any>>} - The transformed appetite data.
 */
export const transformAppetiteData = (appetiteData) => {
  return (
    appetiteData?.map((app) => ({
      customer_name:
        truncateString(app.applicant_info?.name) || app.customer_name,
      lob: app.lob,
      agent_name: app.agent_name,
      application_classification: app.application_classification,
      application_status: app.application_status,
      priority: app.priority,
      date: app.effective_date,
      application_id: app.application_id,
      dnw_decision: app.dnw_decision,
    })) || []
  );
};

/**
 * Constructs a query string based on the provided filter criteria.
 *
 * @param {Object} filters - The filter criteria used to generate query parameters.
 * @returns {string} The encoded query string with the applied filters.
 */
export const generateDateQueryParams = (filters) => {
  // Construct query parameters dynamically
  const queryParams = new URLSearchParams();

  if (filters.selected === "date") {
    if (
      filters.filterBy === "custom" ||
      (filters.startDate && filters.endDate)
    ) {
      if (filters.startDate) {
        queryParams.append(
          "start_date",
          new Date(filters.startDate).toISOString()
        );
      }
      if (filters.endDate) {
        queryParams.append("end_date", new Date(filters.endDate).toISOString());
      }
    } else {
      if (filters.filterBy) {
        const { startDate, endDate } = getDateRange(filters.filterBy);
        queryParams.append("start_date", startDate);
        queryParams.append("end_date", endDate);
      }
    }
  }
  const encodedQueryString = queryParams.toString().replace(/\+/g, "%20");
  return `${encodedQueryString.toString()}`;
};
